import { MerchantPlatform } from "./merchantBranding";

export type HelpCategoryId =
  | "getting-started"
  | "orders"
  | "labels"
  | "notifications"
  | "settings"
  | "integrations"
  | "troubleshooting";

export interface HelpCategory {
  id: HelpCategoryId;
  title: string;
  description: string;
  icon: "rocket" | "orders" | "label" | "bell" | "gear" | "plug" | "wrench";
}

export interface HelpArticle {
  id: string;
  category: HelpCategoryId;
  title: string;
  summary: string;
  /** Platforms that see this article. Empty = all live platforms. */
  platforms?: MerchantPlatform[];
  tags: string[];
  steps?: string[];
  body?: string[];
  tips?: string[];
}

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    title: "Getting started",
    description: "Set up your store and understand the merchant portal",
    icon: "rocket",
  },
  {
    id: "orders",
    title: "Orders & deliveries",
    description: "Track, filter, and manage live deliveries",
    icon: "orders",
  },
  {
    id: "labels",
    title: "Shipping labels",
    description: "Print and download labels for drivers",
    icon: "label",
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "Stay updated on delivery events",
    icon: "bell",
  },
  {
    id: "settings",
    title: "Settings & pricing",
    description: "Store profile, rates, and preferences",
    icon: "gear",
  },
  {
    id: "integrations",
    title: "Platform integration",
    description: "Connect Shopify, custom APIs, and more",
    icon: "plug",
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description: "Fix common issues quickly",
    icon: "wrench",
  },
];

const SHARED_ARTICLES: HelpArticle[] = [
  {
    id: "portal-overview",
    category: "getting-started",
    title: "Merchant portal overview",
    summary:
      "Learn what each tab does: Dashboard, Orders, Notifications, Settings, and Help.",
    tags: ["overview", "navigation", "portal"],
    steps: [
      "Open Dashboard for a snapshot of in-transit, delivered, and total orders.",
      "Use Orders to search, filter by status or date, and open order details.",
      "Check Notifications for delivery updates you have not read yet.",
      "Open Settings to edit store profile, pricing, labels, and notification preferences.",
      "Use Help anytime for step-by-step guides for your store type.",
    ],
    tips: [
      "Use the date filter on Dashboard and Orders to focus on today, a week, or a custom range.",
      "Log out from the sidebar when you finish on a shared computer.",
    ],
  },
  {
    id: "view-orders",
    category: "orders",
    title: "Find and open deliveries",
    summary: "Filter orders by status and date, then open details for an order.",
    tags: ["orders", "filter", "status", "search"],
    steps: [
      "Go to Orders in the left sidebar.",
      "Choose a status filter such as In transit, Delivered, or All.",
      "Adjust the date range if you need a different period.",
      "Click an order row to open details, address, and delivery status.",
      "Use Refresh if you just received a paid order and do not see it yet.",
    ],
    tips: [
      "Recent orders on the Dashboard open the same detail sheet as the Orders page.",
    ],
  },
  {
    id: "print-labels",
    category: "labels",
    title: "Print or download shipping labels",
    summary: "Generate labels from an order so drivers can pick up and deliver.",
    tags: ["label", "print", "pdf", "shipping"],
    steps: [
      "Open an order from Dashboard or Orders.",
      "Choose a label size in Settings → Labels if you have a preferred format.",
      "From the order details, download or print the shipping label.",
      "Confirm the pickup address and customer address look correct before handing off.",
    ],
    tips: [
      "You can change default label formats anytime under Settings → Labels.",
    ],
  },
  {
    id: "notifications-basics",
    category: "notifications",
    title: "Use delivery notifications",
    summary: "See unread updates and jump to the related order.",
    tags: ["notifications", "alerts", "unread"],
    steps: [
      "Open Notifications from the sidebar (badge shows unread count).",
      "Review new delivery events such as status changes.",
      "Open an item to jump to related orders when available.",
      "Configure which events you want under Settings → Notifications.",
    ],
  },
  {
    id: "settings-profile",
    category: "settings",
    title: "Update store profile and pickup",
    summary: "Keep your store name and default pickup location accurate for drivers.",
    tags: ["settings", "profile", "pickup", "address"],
    steps: [
      "Go to Settings → Profile.",
      "Confirm store name and contact details.",
      "Search and select the default pickup location if it changed.",
      "Save your changes before leaving the page.",
    ],
    tips: [
      "Incorrect pickup coordinates can delay driver assignment — always pick an address from search results.",
    ],
  },
  {
    id: "settings-pricing",
    category: "settings",
    title: "Review checkout pricing",
    summary: "Understand how Zulu delivery rates appear for your customers.",
    tags: ["pricing", "rates", "checkout"],
    steps: [
      "Open Settings → Pricing.",
      "Review active delivery services and default rates for this store.",
      "Confirm weight charges or tax settings if your store uses them.",
      "Contact Zulu admin if you need rate changes that only ops can edit.",
    ],
    body: [
      "Checkout pricing status on the Dashboard shows whether rates are ready for customers. If pricing shows a warning, fix issues listed in Settings or ask Zulu support.",
    ],
  },
  {
    id: "troubleshoot-missing-order",
    category: "troubleshooting",
    title: "Order is missing in the portal",
    summary: "What to check when a paid order does not appear under Orders.",
    tags: ["missing", "import", "orders", "paid"],
    steps: [
      "Confirm the order is paid / fulfilled enough for Zulu to import.",
      "Refresh Dashboard or Orders and widen the date filter.",
      "Check that the order used Zulu delivery at checkout (not another carrier only).",
      "Verify store connection status on the Dashboard is healthy.",
      "If it still missing, email support with the platform order number and store name.",
    ],
  },
  {
    id: "troubleshoot-login",
    category: "troubleshooting",
    title: "Cannot open the merchant portal",
    summary: "Fix session, link, or browser issues when opening your store dashboard.",
    tags: ["login", "session", "portal"],
    steps: [
      "Use the latest dashboard link from onboarding or your bookmark for this store.",
      "Allow cookies for the Zulu merchant domain.",
      "Try a private window if an old session is stuck.",
      "For custom stores, sign in again from /merchant with your Zulu account if needed.",
      "Contact support if the session token expired and you no longer have a valid link.",
    ],
  },
];

const SHOPIFY_ARTICLES: HelpArticle[] = [
  {
    id: "shopify-install",
    category: "getting-started",
    title: "Install Zulu on your Shopify store",
    summary: "Connect the Zulu app from Shopify Admin so rates and orders sync.",
    platforms: ["shopify"],
    tags: ["shopify", "install", "app", "plugin"],
    steps: [
      "From Zulu Store → Connect Shopify, enter your myshopify.com store name.",
      "Approve the Zulu app install in Shopify Admin when prompted.",
      "Finish any onboarding steps Zulu shows (pickup, delivery defaults).",
      "Confirm Dashboard shows a healthy Store connection status.",
      "Place a test checkout with Zulu delivery to verify rates appear.",
    ],
    tips: [
      "You must be a Shopify store owner or staff with app install permission.",
      "If install opens the wrong shop, check the store handle you typed.",
    ],
  },
  {
    id: "shopify-checkout-rates",
    category: "integrations",
    title: "Show Zulu rates at Shopify checkout",
    summary: "Make sure carrier / delivery rates from Zulu appear to buyers.",
    platforms: ["shopify"],
    tags: ["shopify", "checkout", "rates", "carrier"],
    steps: [
      "In Shopify Admin, confirm Zulu is installed and not uninstalled.",
      "Open Zulu merchant Settings → Pricing and confirm services are active.",
      "Ensure the shipping destination is inside Zulu’s service area.",
      "Run a test checkout with a deliverable Toronto-area (or covered) address.",
      "If rates are missing, check Dashboard → Checkout pricing status for warnings.",
    ],
    tips: [
      "Weight, postal code, and cart contents can affect which rates return.",
    ],
  },
  {
    id: "shopify-order-import",
    category: "integrations",
    title: "How Shopify orders import into Zulu",
    summary: "Paid Shopify orders that use Zulu delivery appear in Orders automatically.",
    platforms: ["shopify"],
    tags: ["shopify", "import", "webhook", "paid"],
    steps: [
      "Customer completes checkout choosing a Zulu delivery option.",
      "Payment succeeds in Shopify.",
      "Zulu receives the order and creates a delivery in your merchant portal.",
      "Open Orders to track status, print labels, and follow the delivery.",
    ],
    body: [
      "Draft or unpaid checkouts do not import. Cancelled Shopify orders may stop progressing in Zulu — check order details if status looks stuck.",
    ],
  },
  {
    id: "shopify-troubleshoot-rates",
    category: "troubleshooting",
    title: "Shopify checkout shows no Zulu rates",
    summary: "Quick checks when buyers do not see Zulu shipping options.",
    platforms: ["shopify"],
    tags: ["shopify", "rates", "checkout", "error"],
    steps: [
      "Confirm the Zulu app is still installed on the correct Shopify store.",
      "Check Dashboard store connection and checkout pricing status.",
      "Verify Settings → Pricing has at least one active delivery service.",
      "Test with a postal code inside your coverage area.",
      "Ask Zulu admin to confirm carrier service / rate configuration for this shop.",
    ],
  },
];

const CUSTOM_ARTICLES: HelpArticle[] = [
  {
    id: "custom-add-store",
    category: "getting-started",
    title: "Add your shop with an admin access key",
    summary:
      "Existing Zulu customers need a one-time key from admin before creating a custom store.",
    platforms: ["custom"],
    tags: ["custom", "access key", "invite", "store"],
    steps: [
      "Ask Zulu admin to create an access key locked to your Zulu customer ID.",
      "Sign in to your Zulu customer account and open Store.",
      "Choose Add your store to Zulu and paste the access key.",
      "Enter store name and default pickup location, then submit.",
      "After congratulations, open the merchant dashboard in a new tab.",
    ],
    tips: [
      "Each key works once and only for the Zulu ID it was created for.",
      "If a key was already used or expired, ask admin for a new one.",
    ],
  },
  {
    id: "custom-api-key",
    category: "integrations",
    title: "Use your Zulu API key (rates & orders)",
    summary:
      "Your custom store API key authenticates checkout rates and paid-order webhooks.",
    platforms: ["custom"],
    tags: ["custom", "api key", "bearer", "rates", "webhooks"],
    steps: [
      "Copy the API key when Zulu shows it (it is revealed only once).",
      "Store it in your backend secrets — never in public frontend code.",
      "Send it as a Bearer token on Zulu custom rate and webhook requests.",
      "If the key is lost, contact Zulu admin to rotate / re-issue credentials.",
    ],
    body: [
      "Zulu never stores the raw key for display again after the one-time reveal. Treat it like a password.",
    ],
    tips: [
      "Rotate the key if it may have been exposed in logs or a client app.",
    ],
  },
  {
    id: "custom-rates-api",
    category: "integrations",
    title: "Request delivery rates from your storefront",
    summary: "Call Zulu’s custom rates API from your checkout backend.",
    platforms: ["custom"],
    tags: ["custom", "rates", "api", "checkout"],
    steps: [
      "From your server, POST to Zulu’s custom rates endpoint with your API key.",
      "Include destination address, package weight/dimensions, and cart context as required.",
      "Show returned Zulu delivery options to the shopper at checkout.",
      "When the shopper selects a Zulu option, keep that selection with the order.",
    ],
    tips: [
      "Always call rates from your backend so the API key stays private.",
    ],
  },
  {
    id: "custom-webhooks",
    category: "integrations",
    title: "Send paid orders to Zulu",
    summary: "After payment, notify Zulu so the delivery appears in Orders.",
    platforms: ["custom"],
    tags: ["custom", "webhook", "orders", "paid"],
    steps: [
      "When payment succeeds in your platform, POST the paid order to Zulu’s custom webhook endpoint.",
      "Include the selected Zulu delivery method and accurate pickup / dropoff addresses.",
      "Authenticate with your store API key.",
      "Confirm the delivery appears under Orders in the merchant portal.",
      "Print the shipping label and prepare the package for pickup.",
    ],
  },
  {
    id: "custom-troubleshoot-key",
    category: "troubleshooting",
    title: "Access key or API key errors",
    summary: "Fix invalid key, wrong customer, or unauthorized API responses.",
    platforms: ["custom"],
    tags: ["custom", "key", "401", "403"],
    steps: [
      "For Add store: confirm you pasted the full access key and are logged into the matching Zulu account.",
      "Ask admin to issue a fresh key if yours expired, was revoked, or already used.",
      "For API calls: send Authorization: Bearer <api_key> with no extra spaces.",
      "Confirm you are hitting the correct Zulu environment (local vs production).",
      "Contact support with the error code (for example CUSTOM_JOIN_KEY_WRONG_CUSTOMER).",
    ],
  },
];

const WOOCOMMERCE_ARTICLES: HelpArticle[] = [
  {
    id: "woo-getting-started",
    category: "getting-started",
    title: "WooCommerce with Zulu",
    summary: "Overview of connecting WooCommerce stores to Zulu delivery.",
    platforms: ["woocommerce"],
    tags: ["woocommerce", "plugin"],
    steps: [
      "Install and authorize the Zulu WooCommerce connection when available for your store.",
      "Complete onboarding with pickup address and delivery defaults.",
      "Confirm Dashboard connection status is healthy.",
      "Place a test order using Zulu delivery to verify import.",
    ],
    body: [
      "If WooCommerce install is still rolling out for your account, contact Zulu support for enablement.",
    ],
  },
  {
    id: "woo-orders",
    category: "integrations",
    title: "WooCommerce order sync",
    summary: "Paid WooCommerce orders using Zulu delivery appear in the portal.",
    platforms: ["woocommerce"],
    tags: ["woocommerce", "orders", "sync"],
    steps: [
      "Customer checks out with a Zulu shipping method.",
      "Payment completes in WooCommerce.",
      "Zulu imports the delivery into Orders.",
      "Manage labels and tracking from the merchant portal.",
    ],
  },
];

export const ALL_HELP_ARTICLES: HelpArticle[] = [
  ...SHARED_ARTICLES,
  ...SHOPIFY_ARTICLES,
  ...CUSTOM_ARTICLES,
  ...WOOCOMMERCE_ARTICLES,
];

export function articlesForPlatform(platform: MerchantPlatform): HelpArticle[] {
  return ALL_HELP_ARTICLES.filter((article) => {
    if (!article.platforms || article.platforms.length === 0) {
      return true;
    }
    return article.platforms.includes(platform);
  });
}

export function supportMailto(platformLabel: string): string {
  return `mailto:support@zulucourier.com?subject=${encodeURIComponent(
    `${platformLabel} merchant help`
  )}`;
}
